/**
 * Converts a relative URL to an absolute URL using the provided base URL.
 * If the URL is already absolute, returns it unchanged.
 *
 * @param url - The URL to convert (can be relative or absolute)
 * @param baseUrl - The base URL to use for relative URLs
 * @returns The absolute URL, or the original URL if conversion fails
 *
 * @example
 * makeAbsoluteUrl('/uploads/image.png', 'https://example.com/')
 * // Returns: 'https://example.com/uploads/image.png'
 *
 * makeAbsoluteUrl('https://cdn.example.com/image.png', 'https://example.com/')
 * // Returns: 'https://cdn.example.com/image.png' (unchanged)
 *
 * makeAbsoluteUrl('//cdn.example.com/image.png', 'https://example.com/')
 * // Returns: 'https://cdn.example.com/image.png' (inherits base URL's protocol)
 *
 * makeAbsoluteUrl('', 'https://example.com/')
 * // Returns: ''
 *
 * @note Protocol-relative URLs (//example.com) will inherit the base URL's protocol.
 * This intentionally converts protocol-agnostic URLs to use a specific protocol.
 */
export const makeAbsoluteUrl = (url: string | undefined, baseUrl: string): string => {
  if (!url) return '';

  try {
    // If it's already an absolute URL, return as-is
    new URL(url);
    return url;
  } catch {
    // If URL parsing fails, it's likely a relative URL
    try {
      const absoluteUrl = new URL(url, baseUrl);
      return absoluteUrl.href;
    } catch {
      // If both fail, return the original URL
      return url;
    }
  }
};
